import {computed, effect, EventEmitter, inject, Injectable, InjectionToken, signal} from '@angular/core';
import {IToolbarLocalStorageState, IToolbarState} from './interfaces';
import {ECalendar} from '../../calendar/calendar.type';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {EGranularity, FromNgbDate, ICalendarChangeEvent, ToNgbDate} from 'frontier/browserkit';
import {getQuarter, IFilter} from 'frontier/nucleus';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {DateTime} from 'luxon';
import {BUILD_HASH_TOKEN} from 'frontier/nucleus/src/lib/configurations/build-hash';

export const INITIAL_TOOLBAR_STATE_TOKEN = new InjectionToken<IToolbarState>('initialToolbarState');

const today = new Date();

export const DEFAULT_TOOLBAR_STATE: IToolbarState = {
  calendarType: ECalendar.classic,
  classicalCalendar: {
    fromDate: new NgbDate(
      today.getFullYear() - 1,
      today.getMonth() + 1,
      today.getDate(),
    ),
    toDate: new NgbDate(
      today.getFullYear(),
      today.getMonth() + 1,
      today.getDate(),
    ),
    granularity: EGranularity.month,
  },
  easyDatePicker: {
    year: [today.getFullYear()],
    month: [today.getMonth() + 1],
    quarter: ['Q' + getQuarter()],
  },
  filter: {},
}

class LocalStorageManager {
  readonly injectedInitalState = inject(INITIAL_TOOLBAR_STATE_TOKEN, {optional: true});
  readonly buildHash = inject(BUILD_HASH_TOKEN, {optional: false});

  readonly LOCALSTORAGE_KEY = 'toolbar';

  getInitialState(): IToolbarState {
    return this.injectedInitalState || DEFAULT_TOOLBAR_STATE
  }

  toStorage(state: IToolbarState): void {
    const toolbarSate: IToolbarLocalStorageState = {
      ...state,
      classicalCalendar: {
        fromDate: FromNgbDate(state.classicalCalendar.fromDate).toISO(),
        toDate: FromNgbDate(state.classicalCalendar.toDate).toISO(),
        granularity: state.classicalCalendar.granularity,
      },
      buildHash: this.buildHash,
    }
    localStorage.setItem(this.LOCALSTORAGE_KEY, JSON.stringify(toolbarSate));
  }

  fromStorage(): IToolbarState {
    try {
      const localStorageState: IToolbarLocalStorageState = JSON.parse(localStorage.getItem(this.LOCALSTORAGE_KEY));
      if (this.buildHash !== localStorageState.buildHash) {
        return this.getInitialState();
      }
      return {
        ...localStorageState,
        classicalCalendar: {
          fromDate: ToNgbDate(DateTime.fromISO(localStorageState.classicalCalendar.fromDate)),
          toDate: ToNgbDate(DateTime.fromISO(localStorageState.classicalCalendar.toDate)),
          granularity: localStorageState.classicalCalendar.granularity,
        }
      }
    } catch (e) {
      console.warn('Failed to parse toolbar state from local storage, returning initial state');
      return this.getInitialState();
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class ToolbarStore {
  private _localStorageManager = new LocalStorageManager();
  state = signal<IToolbarState>(this._localStorageManager.fromStorage());
  state$ = toObservable(this.state);

  // actions
  resetFullTextFilter$ = new EventEmitter();

  // selectors
  searchFilter = computed(() => this.state().filter?.searchstring);

  toolbarFilterDto = computed(() => {
    const state = this.state();
    const apiFilter: IFilter = {
      objectlist: [],
      dateranges: undefined,
      granularity: undefined,
    };
    if (state.calendarType === ECalendar.easy) {
      if (
        state.easyDatePicker.month.length !== 0 &&
        state.easyDatePicker.year.length !== 0
      ) {
        apiFilter.dateranges = {
          months: state.easyDatePicker.month,
          years: state.easyDatePicker.year,
        };
        apiFilter.startdate = null;
        apiFilter.enddate = null;
      }
    } else {
      // classical calendar
      // Initial setting of the filter for the table instance.
      const calender = state.classicalCalendar;
      const outEvt: ICalendarChangeEvent = {
        from: FromNgbDate(calender.fromDate).toFormat('yyyy-MM-dd'),
        to: FromNgbDate(calender.toDate).toFormat('yyyy-MM-dd')
      };
      apiFilter.granularity = state.classicalCalendar.granularity;
      apiFilter.startdate = outEvt.from;
      apiFilter.enddate = outEvt.to;
    }

    apiFilter.searchstring = state.filter.searchstring;

    return apiFilter;
  })

  toolbarFilterDto$ = toObservable(this.toolbarFilterDto);

  constructor() {
    effect(() => {
      const state = this.state();
      this._localStorageManager.toStorage(state);
    });

    this.resetFullTextFilter$.pipe(
      takeUntilDestroyed()
    ).subscribe(() => this.resetFullTextFilter())
  }

  private resetFullTextFilter() {
    if (!this.state().filter) return;
    this.state.update(state => {
      return {
        ...state,
        filter: {
          ...state.filter,
          searchstring: ''
        }
      }
    })
  }
}
